import { apiRequest } from './axiosConfig';
import { handleApiError } from '../../utils/errorHandling';

// ユーザー詳細に紐づく組織の簡易情報
export interface AdminUserDetailOrganization {
  id: number;
  name: string;
}

export interface AdminUserDetailUser {
  id: number;
  name: string;
  email: string;
  role: string | null;
  plan: string | null;
  photo_url: string | null;
  first_login_at: string | null;
  created_at: string;
  organization: AdminUserDetailOrganization | null;
}

export interface AdminContentCounts {
  banner: number;
  image: number;
  carousel: number;
  video: number;
}

export interface AdminUserDetail {
  user: AdminUserDetailUser;
  last_login_at: string | null;
  content_counts: AdminContentCounts;
}

// ログイン履歴行。organization向けエンドポイントのみ user を含む
export interface AdminLoginHistoryRow {
  id: number;
  login_type: string;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  user?: {
    id: number;
    name: string;
    email: string;
  } | null;
}

export type AdminContentType = 'banner' | 'image' | 'carousel' | 'video';

export interface AdminContentRow {
  id: number;
  type: AdminContentType;
  thumbnail_url: string | null;
  url: string;
  title: string | null;
  status: string;
  created_at: string;
  user: {
    id: number;
    name: string;
  } | null;
}

// Laravel paginator のJSON表現をそのまま受け取る
export interface PaginatedResult<T> {
  data: T[];
  current_page: number;
  last_page: number;
  total: number;
  per_page: number;
}

const DEFAULT_PER_PAGE = 10;

// 注意: このAPI群は CreditManagementController 系と異なり `{ data: ... }` の
// エンベロープを使わず、コントローラが response()->json() で直接オブジェクト/
// Laravel paginator を返す（AdminUserDetailController / AdminOrganizationContentController
// 実装に準拠）。そのため apiRequest.get() の結果をそのまま対象の型にキャストする。
const adminUserDetailService = {
  /**
   * ユーザー詳細（プロフィール・最終ログイン・生成物件数サマリ）を取得する
   */
  async getUserDetail(userId: number): Promise<AdminUserDetail> {
    try {
      const response = await apiRequest.get(`admin/users/${userId}/detail`);
      return response as unknown as AdminUserDetail;
    } catch (error) {
      throw handleApiError(error, 'ユーザー詳細の取得に失敗しました');
    }
  },

  /**
   * ユーザーのログイン履歴を取得する
   */
  async getUserLoginHistories(
    userId: number,
    params?: { page?: number; per_page?: number }
  ): Promise<PaginatedResult<AdminLoginHistoryRow>> {
    try {
      const response = await apiRequest.get(`admin/users/${userId}/login-histories`, {
        params: { per_page: DEFAULT_PER_PAGE, ...params },
      });
      return response as unknown as PaginatedResult<AdminLoginHistoryRow>;
    } catch (error) {
      throw handleApiError(error, 'ログイン履歴の取得に失敗しました');
    }
  },

  /**
   * 組織のログイン履歴を取得する（メンバーの user 情報付き）
   */
  async getOrganizationLoginHistories(
    organizationId: number,
    params?: { page?: number; per_page?: number }
  ): Promise<PaginatedResult<AdminLoginHistoryRow>> {
    try {
      const response = await apiRequest.get(`admin/organizations/${organizationId}/login-histories`, {
        params: { per_page: DEFAULT_PER_PAGE, ...params },
      });
      return response as unknown as PaginatedResult<AdminLoginHistoryRow>;
    } catch (error) {
      throw handleApiError(error, 'ログイン履歴の取得に失敗しました');
    }
  },

  /**
   * ユーザーの生成物（バナー/画像/カルーセル/動画）を取得する
   */
  async getUserContents(
    userId: number,
    type: AdminContentType,
    params?: { page?: number; per_page?: number }
  ): Promise<PaginatedResult<AdminContentRow>> {
    try {
      const response = await apiRequest.get(`admin/users/${userId}/contents`, {
        params: { type, per_page: DEFAULT_PER_PAGE, ...params },
      });
      return response as unknown as PaginatedResult<AdminContentRow>;
    } catch (error) {
      throw handleApiError(error, '生成物の取得に失敗しました');
    }
  },

  /**
   * 組織の生成物（バナー/画像/カルーセル/動画）を取得する
   */
  async getOrganizationContents(
    organizationId: number,
    type: AdminContentType,
    params?: { page?: number; per_page?: number }
  ): Promise<PaginatedResult<AdminContentRow>> {
    try {
      const response = await apiRequest.get(`admin/organizations/${organizationId}/contents`, {
        params: { type, per_page: DEFAULT_PER_PAGE, ...params },
      });
      return response as unknown as PaginatedResult<AdminContentRow>;
    } catch (error) {
      throw handleApiError(error, '生成物の取得に失敗しました');
    }
  },
};

export default adminUserDetailService;
